/**
 * "Fly to cart" effect (Tokopedia-style): a thumbnail of the product leaves the
 * element that was tapped, arcs across the screen and shrinks into the cart icon
 * in the top bar, which then bumps.
 *
 * The cart icon registers itself with `registerCartIcon` (callback ref on the
 * button in TopBarHeader). Pages call `flyToCart` right before adding the item.
 * Runs on the Web Animations API, so it is a no-op where that is missing (jsdom).
 */

let cartIcon: HTMLElement | null = null;

/** Callback ref for the cart button in the top bar: the landing spot of the thumbnail. */
export const registerCartIcon = (element: HTMLElement | null) => {
  cartIcon = element;
};

interface FlyToCartOptions {
  /** Product cover; without one a plain blue dot flies instead */
  imageUrl?: string | null;
  /** Element the thumbnail takes off from (the cover image or the button that was tapped) */
  from: HTMLElement;
}

const FLIGHT_MS = 700;
const THUMB_MAX = 64;

const canAnimate = (element: HTMLElement) => typeof element.animate === 'function';

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Small scale pulse on the cart icon when the thumbnail lands */
const bump = (element: HTMLElement) => {
  if (!canAnimate(element)) return;
  element.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }],
    { duration: 300, easing: 'ease-out' }
  );
};

export const flyToCart = ({ imageUrl, from }: FlyToCartOptions) => {
  const target = cartIcon;
  if (!target || !target.isConnected || !canAnimate(from)) return;

  if (prefersReducedMotion()) {
    bump(target);
    return;
  }

  const start = from.getBoundingClientRect();
  const end = target.getBoundingClientRect();
  const size = Math.max(24, Math.min(THUMB_MAX, start.width, start.height));

  const startX = start.left + start.width / 2 - size / 2;
  const startY = start.top + start.height / 2 - size / 2;
  const dx = end.left + end.width / 2 - size / 2 - startX;
  const dy = end.top + end.height / 2 - size / 2 - startY;

  const ghost = document.createElement(imageUrl ? 'img' : 'div');
  if (ghost instanceof HTMLImageElement && imageUrl) {
    ghost.src = imageUrl;
    ghost.alt = '';
  }
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${startX}px`,
    top: `${startY}px`,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '12px',
    objectFit: 'cover',
    background: imageUrl ? 'transparent' : 'var(--color-text-blue-100)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
    pointerEvents: 'none',
    zIndex: '9999',
    willChange: 'transform, opacity',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(ghost);

  // Arc: lift up and forward first, then drop into the icon while shrinking
  const lift = Math.min(140, Math.abs(dy) * 0.35 + 60);
  const flight = ghost.animate(
    [
      { transform: 'translate(0, 0) scale(1)', opacity: 1, easing: 'ease-out' },
      {
        transform: `translate(${dx * 0.55}px, ${dy * 0.45 - lift}px) scale(0.65)`,
        opacity: 1,
        easing: 'ease-in',
      },
      { transform: `translate(${dx}px, ${dy}px) scale(0.15)`, opacity: 0.3 },
    ],
    { duration: FLIGHT_MS, fill: 'forwards' }
  );

  flight.onfinish = () => {
    ghost.remove();
    bump(target);
  };
  flight.oncancel = () => ghost.remove();
};
